import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { TrendingUp, TrendingDown, Minus, Newspaper, Sparkles, RefreshCw } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fetchBias } from "@/lib/api";
import { SkeletonBar } from "@/components/cot/Skeleton";

const CURRENCIES = ["USD", "EUR", "GBP", "JPY", "XAU"];
const REFETCH_MS = 1000 * 60 * 60 * 4; // live re-scan cadence (every 4h to conserve LLM credits)

const agoLabel = (ts) => {
  if (!ts) return "";
  const secs = Math.max(0, Math.floor(Date.now() / 1000 - ts));
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
};

const mmss = (secs) => {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const styleFor = (bias, ccy) => {
  if (bias === "bullish")
    return { Icon: TrendingUp, label: `${ccy} ▲`, cls: "text-pos", border: "border-emerald-500/50", bg: "bg-emerald-950/20" };
  if (bias === "bearish")
    return { Icon: TrendingDown, label: `${ccy} ▼`, cls: "text-neg", border: "border-red-500/50", bg: "bg-red-950/20" };
  return { Icon: Minus, label: `${ccy} →`, cls: "text-amber-term", border: "border-zinc-700", bg: "bg-[#09090B]" };
};

export default function USDNewsBanner({ embedded = false }) {
  const qc = useQueryClient();
  const [currency, setCurrency] = useState("USD");
  const [refreshing, setRefreshing] = useState(false);
  const [now, setNow] = useState(Date.now());
  const q = useQuery({
    queryKey: ["bias", currency],
    queryFn: () => fetchBias(currency),
    staleTime: REFETCH_MS,
    refetchInterval: REFETCH_MS,
  });
  const d = q.data;
  const bias = d?.bias || "neutral";
  const conf = d?.confidence ?? 0;
  const s = styleFor(bias, currency);
  const Icon = s.Icon;
  const loading = q.isLoading || q.isFetching || refreshing;
  const reason = d?.reasoning || (loading ? "" : "—");
  const drivers = d?.top_drivers || [];
  const headlines = d?.headlines || [];

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const secsLeft = q.dataUpdatedAt
    ? Math.max(0, Math.round(REFETCH_MS / 1000 - (now - q.dataUpdatedAt) / 1000))
    : null;

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const fresh = await fetchBias(currency, true);
      qc.setQueryData(["bias", currency], fresh);
    } catch {
      /* ignore */
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className={`${embedded ? "" : `border ${s.border} `}${s.bg}`} data-testid="usd-news-banner">
      <div className="px-3 py-2 flex items-center gap-3 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-zinc-500">
          <Newspaper size={12} strokeWidth={1.5} /> MACRO BIAS
        </span>
        <Select value={currency} onValueChange={setCurrency}>
          <SelectTrigger
            className="h-6 w-[78px] bg-black border-zinc-700 text-[11px] font-mono uppercase tracking-wider text-zinc-200 focus:ring-amber-500"
            data-testid="bias-currency-select"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-black border-zinc-700 text-zinc-200 font-mono">
            {CURRENCIES.map((c) => (
              <SelectItem key={c} value={c} className="text-xs focus:bg-zinc-800 focus:text-amber-term" data-testid={`bias-currency-${c}`}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className={`inline-flex items-center gap-1 text-base font-semibold ${s.cls}`} data-testid="usd-bias-arrow">
          <Icon size={16} strokeWidth={2} />
          {s.label}
        </span>
        <span className="text-xs tabular-nums text-zinc-300" data-testid="usd-bias-conf">
          <span className="text-zinc-500 uppercase tracking-widest text-[11px] mr-1">CONF</span>
          {conf}%
        </span>
        <span className="text-[11px] tabular-nums text-zinc-500 uppercase tracking-widest">
          <span className="text-pos">{d?.bullish_count ?? 0}↑</span>
          <span className="mx-1 text-zinc-700">/</span>
          <span className="text-neg">{d?.bearish_count ?? 0}↓</span>
          <span className="mx-1 text-zinc-700">/</span>
          <span className="text-zinc-400">{d?.neutral_count ?? 0}→</span>
        </span>
        <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-600 ml-auto">
          <span className="inline-flex items-center gap-1.5" data-testid="usd-bias-live" title="Auto re-scans on a live cycle">
            <span
              className={`inline-block h-1.5 w-1.5 rounded-full ${loading ? "bg-amber-500 animate-ping" : "bg-[#52525b] animate-pulse"}`}
            />
            <span className={loading ? "text-amber-term" : "text-pos"}>
              {loading ? "SCANNING" : "LIVE"}
            </span>
            {!loading && secsLeft != null && (
              <span className="text-zinc-500 tabular-nums normal-case tracking-normal">
                next {mmss(secsLeft)}
              </span>
            )}
          </span>
          <span className="hidden md:inline">·</span>
          <span className="hidden md:inline">
            <Sparkles size={10} strokeWidth={1.5} className="inline mr-1" />
            {headlines.length} hl
          </span>
          {d?.generated_at && (
            <span className="text-zinc-500 normal-case tracking-normal" data-testid="usd-bias-updated">
              · {agoLabel(d.generated_at)}
            </span>
          )}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="usd-bias-refresh"
            title="Refresh macro bias now"
            className="text-zinc-500 hover:text-amber-term transition-colors disabled:opacity-50"
          >
            <RefreshCw size={11} strokeWidth={1.5} className={refreshing ? "animate-spin" : ""} />
          </button>
        </span>
      </div>
      <div className="px-3 pb-2 text-xs text-zinc-300" data-testid="usd-bias-reason">
        {reason ? (
          <>▸ {reason}</>
        ) : (
          <SkeletonBar className="h-3 w-2/3" />
        )}
      </div>
      {drivers.length > 0 && (
        <div className="border-t border-zinc-800 px-3 py-2 flex flex-col sm:flex-row sm:flex-wrap gap-x-4 gap-y-1" data-testid="usd-bias-drivers">
          {drivers.slice(0, 3).map((t, i) => {
            const h = headlines.find((x) => x.title === t);
            const body = (
              <span className="flex items-baseline gap-1.5 min-w-0 max-w-full">
                <span className="text-[11px] uppercase tracking-widest text-zinc-600 shrink-0">#{i + 1}</span>
                <span className="text-[11px] text-zinc-200 truncate">{t}</span>
              </span>
            );
            return h?.link ? (
              <a key={t} href={h.link} target="_blank" rel="noreferrer" className="min-w-0 max-w-full hover:text-amber-term">
                {body}
              </a>
            ) : (
              <span key={t} className="min-w-0 max-w-full">{body}</span>
            );
          })}
        </div>
      )}
    </div>
  );
}
